import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, DatePicker, Tag, Space,
  Typography, Popconfirm, Alert, Tooltip, Switch, message as antMessage,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, CopyOutlined, KeyOutlined,
  CheckCircleOutlined, StopOutlined, EyeOutlined,
} from '@ant-design/icons';
import apiService from '../services/apiService';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

const ApiKeysSettings = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // { name, rawKey }
  const [revealLoading, setRevealLoading] = useState(null); // key _id being revealed
  const [form] = Form.useForm();

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/api-keys');
      setKeys(res.data.data || []);
    } catch {
      antMessage.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      const payload = { name: values.name };
      if (values.expiresAt) payload.expiresAt = values.expiresAt.toISOString();
      await apiService.post('/api-keys', payload);
      setCreateVisible(false);
      form.resetFields();
      fetchKeys();
      antMessage.success('API key created');
    } catch (err) {
      antMessage.error(err.response?.data?.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleReveal = async (record) => {
    setRevealLoading(record._id);
    try {
      const res = await apiService.get(`/api-keys/${record._id}/reveal`);
      setRevealedKey({ name: record.name, rawKey: res.data.data.rawKey });
    } catch {
      antMessage.error('Failed to reveal key');
    } finally {
      setRevealLoading(null);
    }
  };

  const handleToggle = async (record, checked) => {
    try {
      await apiService.patch(`/api-keys/${record._id}`, { isActive: checked });
      setKeys(prev => prev.map(k => k._id === record._id ? { ...k, isActive: checked } : k));
    } catch {
      antMessage.error('Failed to update key');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.delete(`/api-keys/${id}`);
      setKeys(prev => prev.filter(k => k._id !== id));
      antMessage.success('API key deleted');
    } catch {
      antMessage.error('Failed to delete key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => antMessage.success('Copied to clipboard'));
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Key',
      dataIndex: 'keyPrefix',
      key: 'keyPrefix',
      render: (prefix) => (
        <Text code style={{ letterSpacing: 1 }}>{prefix}••••••••••••••••</Text>
      ),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date) => {
        if (!date) return <Tag color="green">Never</Tag>;
        const expired = dayjs(date).isBefore(dayjs());
        return (
          <Tag color={expired ? 'red' : 'orange'}>
            {dayjs(date).format('DD MMM YYYY')}
            {expired ? ' (expired)' : ''}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active, record) => (
        <Switch
          checked={active}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<StopOutlined />}
          onChange={(checked) => handleToggle(record, checked)}
          size="small"
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => dayjs(d).format('DD MMM YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Reveal & copy key">
            <Button
              icon={<EyeOutlined />}
              size="small"
              type="text"
              loading={revealLoading === record._id}
              onClick={() => handleReveal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this API key?"
            description="Any app using this key will stop working immediately."
            onConfirm={() => handleDelete(record._id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small" type="text" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <Text strong style={{ fontSize: 16 }}>
            <KeyOutlined className="mr-2" />
            API Keys
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 13 }}>
            Generate keys for the DPDPA custom app to authenticate against this tenant's API.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateVisible(true)}
        >
          Create API Key
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={keys}
        rowKey="_id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'No API keys yet. Create one to get started.' }}
      />

      {/* Create modal */}
      <Modal
        title={<><PlusOutlined className="mr-2" />Create New API Key</>}
        open={createVisible}
        onCancel={() => { setCreateVisible(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="Key Name"
            rules={[{ required: true, message: 'Please enter a name for this key' }]}
          >
            <Input placeholder="e.g. DPDPA Production App" maxLength={100} />
          </Form.Item>

          <Form.Item name="expiresAt" label="Expiry Date (optional)">
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Leave empty for no expiry"
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setCreateVisible(false); form.resetFields(); }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={creating}>
              Generate Key
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Reveal key modal */}
      <Modal
        title={<><KeyOutlined className="mr-2" />API Key — {revealedKey?.name}</>}
        open={!!revealedKey}
        onCancel={() => setRevealedKey(null)}
        footer={[
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(revealedKey?.rawKey)}
          >
            Copy Key
          </Button>,
          <Button key="close" onClick={() => setRevealedKey(null)}>
            Close
          </Button>,
        ]}
      >
        <div
          style={{
            background: '#0f172a',
            borderRadius: 6,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <Text
            code
            style={{ color: '#86efac', background: 'transparent', fontSize: 13, wordBreak: 'break-all' }}
          >
            {revealedKey?.rawKey}
          </Text>
          <Tooltip title="Copy">
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              style={{ color: '#86efac', flexShrink: 0 }}
              onClick={() => copyToClipboard(revealedKey?.rawKey)}
            />
          </Tooltip>
        </div>
      </Modal>
    </div>
  );
};

export default ApiKeysSettings;
